import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Bot,
  Plus,
  Settings,
  Trash2,
  Edit,
  Power,
  MessageSquare,
  Users,
  Briefcase,
  Building2,
  Shield,
  Headphones,
  Loader2,
  Copy,
  Eye,
  Save,
  X,
  Zap,
  Palette,
  Target,
  Brain,
  Sparkles,
  Code,
  Globe,
  RefreshCw,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// OpenRouter free models
const OPENROUTER_MODELS = [
  { id: "openrouter/free", name: "OpenRouter Free (Auto)", description: "Selección automática" },
  { id: "minimax/minimax-m2.5:free", name: "MiniMax M2.5", description: "Productividad" },
  { id: "minimax/minimax-m2.1", name: "MiniMax M2.1", description: "Código" },
  { id: "deepseek/deepseek-r1:free", name: "DeepSeek R1", description: "Razonamiento" },
  { id: "deepseek/deepseek-chat:free", name: "DeepSeek Chat", description: "Chat general" },
  { id: "meta-llama/llama-3.3-70b-instruct:free", name: "Llama 3.3 70B", description: "Grande" },
  { id: "meta-llama/llama-3.1-8b-instruct:free", name: "Llama 3.1 8B", description: "Eficiente" },
  { id: "qwen/qwen-2.5-72b-instruct:free", name: "Qwen 2.5 72B", description: "Alibaba" },
  { id: "qwen/qwen-2.5-coder-32b-instruct:free", name: "Qwen Coder 32B", description: "Código" },
  { id: "google/gemma-2-9b-it:free", name: "Gemma 2 9B", description: "Google" },
  { id: "microsoft/phi-3-mini-128k-instruct:free", name: "Phi-3 Mini", description: "Microsoft" },
  { id: "mistralai/mistral-7b-instruct:free", name: "Mistral 7B", description: "Mistral AI" },
];

const CATEGORY_OPTIONS = [
  { id: "usuario", name: "Usuarios", icon: Users, color: "text-blue-400" },
  { id: "profesional", name: "Profesionales", icon: Briefcase, color: "text-purple-400" },
  { id: "negocio", name: "Negocios", icon: Building2, color: "text-green-400" },
  { id: "servicio", name: "Servicios", icon: Zap, color: "text-yellow-400" },
  { id: "super_admin", name: "Super Admin", icon: Shield, color: "text-red-400" },
  { id: "soporte", name: "Soporte", icon: Headphones, color: "text-cyan-400" },
  { id: "all", name: "Todos", icon: Globe, color: "text-white" },
];

const DISPLAY_FORMATS = [
  { id: "floating_button", name: "Botón Flotante", description: "Icono en esquina que abre chat" },
  { id: "embedded_window", name: "Ventana Embebida", description: "Widget incrustado en página" },
  { id: "sidebar", name: "Barra Lateral", description: "Panel deslizable desde el lado" },
  { id: "full_page", name: "Página Completa", description: "Interfaz de chat dedicada" },
];

const AIAgentsManager = ({ getAdminHeaders }) => {
  const [activeTab, setActiveTab] = useState("agents");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // OpenRouter config
  const [orConfig, setOrConfig] = useState({ api_key: "", enabled: false, default_model: "openrouter/free" });
  const [orTesting, setOrTesting] = useState(false);
  const [orTestResult, setOrTestResult] = useState(null);
  
  // Agent form state
  const [agentForm, setAgentForm] = useState({
    name: "",
    description: "",
    greeting: "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte?",
    system_prompt: "Eres un asistente de IA útil y amigable para OkChat.",
    target_categories: [],
    display_format: "floating_button",
    models: ["openrouter/free"],
    primary_model: "openrouter/free",
    fallback_model: "",
    avatar_url: "",
    theme_color: "#ef4444",
    temperature: 0.7,
    max_tokens: 2048,
    enabled: true,
    skills: [],
  });

  useEffect(() => {
    fetchAgents();
    fetchOpenRouterConfig();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API}/admin/ai-agents`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenRouterConfig = async () => {
    try {
      const response = await fetch(`${API}/openrouter/config`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setOrConfig(data);
      }
    } catch (error) {
      console.error("Error fetching OpenRouter config:", error);
    }
  };

  const saveOpenRouterConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/openrouter/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(orConfig),
      });
      
      if (response.ok) {
        toast.success("Configuración de OpenRouter guardada");
        fetchOpenRouterConfig();
      } else {
        toast.error("Error al guardar configuración");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const testOpenRouterConnection = async () => {
    setOrTesting(true);
    setOrTestResult(null);
    try {
      const response = await fetch(`${API}/openrouter/test`, {
        method: "POST",
        credentials: "include",
        headers: getAdminHeaders(),
      });
      const data = await response.json();
      setOrTestResult(data);
      if (data.success) {
        toast.success("Conexión exitosa con OpenRouter");
      } else {
        toast.error(data.error || "Error de conexión");
      }
    } catch (error) {
      setOrTestResult({ success: false, error: error.message });
      toast.error("Error al probar conexión");
    } finally {
      setOrTesting(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!agentForm.name) {
      toast.error("El nombre es requerido");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/ai-agents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(agentForm),
      });
      
      if (response.ok) {
        toast.success("Agente IA creado exitosamente");
        setShowCreateModal(false);
        resetForm();
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear agente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/ai-agents/${selectedAgent.agent_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(agentForm),
      });
      
      if (response.ok) {
        toast.success("Agente IA actualizado");
        setShowEditModal(false);
        setSelectedAgent(null);
        resetForm();
        fetchAgents();
      } else {
        toast.error("Error al actualizar agente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!confirm("¿Eliminar este agente IA?")) return;
    
    try {
      const response = await fetch(`${API}/admin/ai-agents/${agentId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAdminHeaders(),
      });
      
      if (response.ok) {
        toast.success("Agente eliminado");
        fetchAgents();
      } else {
        toast.error("Error al eliminar agente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const toggleAgentStatus = async (agent) => {
    try {
      const response = await fetch(`${API}/admin/ai-agents/${agent.agent_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ ...agent, enabled: !agent.enabled }),
      });
      
      if (response.ok) {
        fetchAgents();
        toast.success(agent.enabled ? "Agente deshabilitado" : "Agente habilitado");
      }
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setAgentForm({
      name: agent.name,
      description: agent.description || "",
      greeting: agent.greeting,
      system_prompt: agent.system_prompt,
      target_categories: agent.target_categories || [],
      display_format: agent.display_format,
      models: agent.models || ["openrouter/free"],
      primary_model: agent.primary_model,
      fallback_model: agent.fallback_model || "",
      avatar_url: agent.avatar_url || "",
      theme_color: agent.theme_color || "#ef4444",
      temperature: agent.temperature || 0.7,
      max_tokens: agent.max_tokens || 2048,
      enabled: agent.enabled,
      skills: agent.skills || [],
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setAgentForm({
      name: "",
      description: "",
      greeting: "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte?",
      system_prompt: "Eres un asistente de IA útil y amigable para OkChat.",
      target_categories: [],
      display_format: "floating_button",
      models: ["openrouter/free"],
      primary_model: "openrouter/free",
      fallback_model: "",
      avatar_url: "",
      theme_color: "#ef4444",
      temperature: 0.7,
      max_tokens: 2048,
      enabled: true,
      skills: [],
    });
  };

  const toggleCategory = (categoryId) => {
    setAgentForm(prev => ({
      ...prev,
      target_categories: prev.target_categories.includes(categoryId)
        ? prev.target_categories.filter(c => c !== categoryId)
        : [...prev.target_categories, categoryId]
    }));
  };

  const toggleModel = (modelId) => {
    setAgentForm(prev => ({
      ...prev,
      models: prev.models.includes(modelId)
        ? prev.models.filter(m => m !== modelId)
        : [...prev.models, modelId]
    }));
  };

  const getCategoryIcon = (categoryId) => {
    const cat = CATEGORY_OPTIONS.find(c => c.id === categoryId);
    return cat ? cat.icon : Users;
  };

  const AgentFormContent = () => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Bot className="w-5 h-5 text-red-500" />
          Información Básica
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Nombre del Agente *</Label>
            <Input
              value={agentForm.name}
              onChange={(e) => setAgentForm({...agentForm, name: e.target.value})}
              className="bg-slate-900 border-slate-700 text-white mt-1"
              placeholder="Asistente de Ventas"
            />
          </div>
          <div>
            <Label className="text-slate-300">Color del Tema</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="color"
                value={agentForm.theme_color}
                onChange={(e) => setAgentForm({...agentForm, theme_color: e.target.value})}
                className="w-12 h-10 p-1 bg-slate-900 border-slate-700"
              />
              <Input
                value={agentForm.theme_color}
                onChange={(e) => setAgentForm({...agentForm, theme_color: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white flex-1"
              />
            </div>
          </div>
        </div>
        
        <div>
          <Label className="text-slate-300">Descripción</Label>
          <Input
            value={agentForm.description}
            onChange={(e) => setAgentForm({...agentForm, description: e.target.value})}
            className="bg-slate-900 border-slate-700 text-white mt-1"
            placeholder="Asistente especializado en..."
          />
        </div>
        
        <div>
          <Label className="text-slate-300">Saludo Inicial</Label>
          <Textarea
            value={agentForm.greeting}
            onChange={(e) => setAgentForm({...agentForm, greeting: e.target.value})}
            className="bg-slate-900 border-slate-700 text-white mt-1"
            rows={2}
            placeholder="¡Hola! ¿En qué puedo ayudarte?"
          />
        </div>
      </div>

      {/* System Prompt */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-500" />
          Prompt del Sistema
        </h3>
        
        <Textarea
          value={agentForm.system_prompt}
          onChange={(e) => setAgentForm({...agentForm, system_prompt: e.target.value})}
          className="bg-slate-900 border-slate-700 text-white"
          rows={5}
          placeholder="Eres un asistente especializado en..."
        />
        <p className="text-xs text-slate-500">
          Define la personalidad, conocimientos y comportamiento del agente IA.
        </p>
      </div>

      {/* Target Categories */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-green-500" />
          Categorías Objetivo
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CATEGORY_OPTIONS.map((cat) => {
            const Icon = cat.icon;
            const isSelected = agentForm.target_categories.includes(cat.id);
            return (
              <button
                key={cat.id}
                onClick={() => toggleCategory(cat.id)}
                className={`p-3 rounded-lg border transition-all flex items-center gap-2 ${
                  isSelected 
                    ? "bg-red-600/20 border-red-600 text-white" 
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                <Icon className={`w-4 h-4 ${isSelected ? cat.color : ""}`} />
                <span className="text-sm">{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Display Format */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Palette className="w-5 h-5 text-blue-500" />
          Formato de Visualización
        </h3>
        
        <div className="grid grid-cols-2 gap-2">
          {DISPLAY_FORMATS.map((format) => (
            <button
              key={format.id}
              onClick={() => setAgentForm({...agentForm, display_format: format.id})}
              className={`p-3 rounded-lg border transition-all text-left ${
                agentForm.display_format === format.id
                  ? "bg-red-600/20 border-red-600 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600"
              }`}
            >
              <p className="font-medium text-sm">{format.name}</p>
              <p className="text-xs text-slate-500">{format.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Models */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-500" />
          Modelos IA (OpenRouter)
        </h3>
        
        <div>
          <Label className="text-slate-300 mb-2 block">Modelo Principal</Label>
          <Select
            value={agentForm.primary_model}
            onValueChange={(value) => setAgentForm({...agentForm, primary_model: value})}
          >
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder="Seleccionar modelo" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {OPENROUTER_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id} className="text-white">
                  {model.name} - {model.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-slate-300 mb-2 block">Modelo de Respaldo (opcional)</Label>
          <Select
            value={agentForm.fallback_model || "none"}
            onValueChange={(value) => setAgentForm({...agentForm, fallback_model: value === "none" ? "" : value})}
          >
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder="Sin respaldo" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="none" className="text-slate-400">Sin respaldo</SelectItem>
              {OPENROUTER_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id} className="text-white">
                  {model.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-slate-300 mb-2 block">Modelos Disponibles (Combo)</Label>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-slate-900 rounded-lg border border-slate-700">
            {OPENROUTER_MODELS.map((model) => (
              <label key={model.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-slate-800 rounded">
                <Checkbox
                  checked={agentForm.models.includes(model.id)}
                  onCheckedChange={() => toggleModel(model.id)}
                />
                <span className="text-sm text-slate-300">{model.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Parameters */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-400" />
          Parámetros
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Temperatura ({agentForm.temperature})</Label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={agentForm.temperature}
              onChange={(e) => setAgentForm({...agentForm, temperature: parseFloat(e.target.value)})}
              className="w-full mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">Menor = más preciso, Mayor = más creativo</p>
          </div>
          <div>
            <Label className="text-slate-300">Tokens Máximos</Label>
            <Input
              type="number"
              value={agentForm.max_tokens}
              onChange={(e) => setAgentForm({...agentForm, max_tokens: parseInt(e.target.value)})}
              className="bg-slate-900 border-slate-700 text-white mt-1"
              min={100}
              max={8000}
            />
          </div>
        </div>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-slate-900 rounded-lg">
        <div>
          <p className="text-white font-medium">Agente Activo</p>
          <p className="text-sm text-slate-400">El agente estará disponible para los usuarios</p>
        </div>
        <Switch
          checked={agentForm.enabled}
          onCheckedChange={(checked) => setAgentForm({...agentForm, enabled: checked})}
        />
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Agentes IA
          </h1>
          <p className="text-slate-400 mt-1">Crea y configura agentes de inteligencia artificial</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Crear Agente
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="agents" className="data-[state=active]:bg-red-600">
            <Bot className="w-4 h-4 mr-2" />
            Agentes ({agents.length})
          </TabsTrigger>
          <TabsTrigger value="openrouter" className="data-[state=active]:bg-red-600">
            <Zap className="w-4 h-4 mr-2" />
            OpenRouter
          </TabsTrigger>
          <TabsTrigger value="models" className="data-[state=active]:bg-red-600">
            <Sparkles className="w-4 h-4 mr-2" />
            Modelos
          </TabsTrigger>
        </TabsList>

        {/* Agents Tab */}
        <TabsContent value="agents" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : agents.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <Bot className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No hay agentes IA</h3>
                <p className="text-slate-400 mb-4">Crea tu primer agente de inteligencia artificial</p>
                <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Agente
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.map((agent) => (
                <Card key={agent.agent_id} className="bg-slate-800 border-slate-700 hover:border-red-600/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: agent.theme_color + "20", borderColor: agent.theme_color, borderWidth: 2 }}
                        >
                          <Bot className="w-6 h-6" style={{ color: agent.theme_color }} />
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{agent.name}</h4>
                          <p className="text-xs text-slate-400">{agent.primary_model?.split("/")[1] || "openrouter"}</p>
                        </div>
                      </div>
                      <Badge className={agent.enabled ? "bg-green-600" : "bg-slate-600"}>
                        {agent.enabled ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-400 mb-3 line-clamp-2">
                      {agent.description || agent.greeting.substring(0, 80) + "..."}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {agent.target_categories?.slice(0, 3).map((cat) => {
                        const catInfo = CATEGORY_OPTIONS.find(c => c.id === cat);
                        return (
                          <Badge key={cat} variant="outline" className="text-xs border-slate-600 text-slate-400">
                            {catInfo?.name || cat}
                          </Badge>
                        );
                      })}
                      {agent.target_categories?.length > 3 && (
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          +{agent.target_categories.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-slate-600 text-slate-300"
                        onClick={() => openEditModal(agent)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={agent.enabled ? "text-green-400" : "text-slate-400"}
                        onClick={() => toggleAgentStatus(agent)}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteAgent(agent.agent_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* OpenRouter Tab */}
        <TabsContent value="openrouter" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Configuración de OpenRouter
              </CardTitle>
              <CardDescription className="text-slate-400">
                Conecta con OpenRouter para acceder a múltiples modelos de IA gratuitos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-yellow-600/10 to-orange-600/10 border border-yellow-600/30 rounded-lg">
                <h4 className="text-yellow-400 font-medium mb-2">¿Cómo obtener tu API Key?</h4>
                <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside">
                  <li>Visita <a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="text-yellow-400 underline">openrouter.ai</a></li>
                  <li>Crea una cuenta gratuita (recibirás $1 de crédito)</li>
                  <li>Ve a Dashboard → API Keys</li>
                  <li>Crea una nueva key (empieza con sk-or-v1-...)</li>
                </ol>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">API Key de OpenRouter</Label>
                  <Input
                    type="password"
                    value={orConfig.api_key || ""}
                    onChange={(e) => setOrConfig({...orConfig, api_key: e.target.value})}
                    className="bg-slate-900 border-slate-700 text-white mt-1"
                    placeholder="sk-or-v1-..."
                  />
                  {orConfig.has_api_key && !orConfig.api_key && (
                    <p className="text-xs text-green-400 mt-1">
                      Key configurada: {orConfig.api_key_preview}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label className="text-slate-300">Modelo por Defecto</Label>
                  <Select
                    value={orConfig.default_model}
                    onValueChange={(value) => setOrConfig({...orConfig, default_model: value})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {OPENROUTER_MODELS.map((model) => (
                        <SelectItem key={model.id} value={model.id} className="text-white">
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-900 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Habilitar OpenRouter</p>
                    <p className="text-sm text-slate-400">Permite usar modelos de IA en la plataforma</p>
                  </div>
                  <Switch
                    checked={orConfig.enabled}
                    onCheckedChange={(checked) => setOrConfig({...orConfig, enabled: checked})}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <Button onClick={saveOpenRouterConfig} disabled={saving} className="bg-red-600 hover:bg-red-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Configuración
                </Button>
                <Button onClick={testOpenRouterConnection} disabled={orTesting} variant="outline" className="border-slate-600 text-slate-300">
                  {orTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Probar Conexión
                </Button>
              </div>
              
              {orTestResult && (
                <div className={`p-4 rounded-lg ${orTestResult.success ? "bg-green-600/20 border border-green-600/30" : "bg-red-600/20 border border-red-600/30"}`}>
                  <div className="flex items-center gap-2">
                    {orTestResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={orTestResult.success ? "text-green-400" : "text-red-400"}>
                      {orTestResult.success ? "Conexión exitosa" : "Error de conexión"}
                    </span>
                  </div>
                  {orTestResult.success && (
                    <p className="text-sm text-slate-400 mt-2">
                      Modelo: {orTestResult.model_used} | Respuesta: {orTestResult.response}
                    </p>
                  )}
                  {orTestResult.error && (
                    <p className="text-sm text-red-400 mt-2">{orTestResult.error}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Models Tab */}
        <TabsContent value="models" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Modelos Gratuitos Disponibles
              </CardTitle>
              <CardDescription className="text-slate-400">
                Lista de modelos de IA gratuitos disponibles a través de OpenRouter
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {OPENROUTER_MODELS.map((model) => (
                  <div key={model.id} className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-medium">{model.name}</h4>
                      <Badge variant="outline" className="text-xs border-green-600 text-green-400">
                        Gratis
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-2">{model.description}</p>
                    <code className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded block truncate">
                      {model.id}
                    </code>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Agent Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-red-500" />
              Crear Nuevo Agente IA
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configura un nuevo asistente de inteligencia artificial
            </DialogDescription>
          </DialogHeader>
          
          <AgentFormContent />
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateAgent} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear Agente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" />
              Editar Agente: {selectedAgent?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modifica la configuración del agente IA
            </DialogDescription>
          </DialogHeader>
          
          <AgentFormContent />
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleUpdateAgent} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAgentsManager;
